// Proactive Suggestions domain types — PROACTIVE_SUGGESTIONS_PLAN.md.
// When the user opens the chat page, the app proposes medications they usually
// take around now (and, later, other things they routinely log) so the common
// case is one tap with ZERO Bedrock tokens — the proposal is built from DynamoDB
// (regimens + recent events), never the model.
//
// Pure data only — safe to import from both the API (Lambda) and the web SPA.
// The server-side provider abstraction (SuggestionProvider/SuggestionContext,
// which reference the API's internal `Proposed` type and DynamoDB-loaded context)
// lives in `api/src/lib/suggestions.ts`; this module holds the wire shapes and
// the per-patient settings both sides share.

import { SCHEDULED_GROUPS, type ScheduledGroup } from "./medication.js";
import { CATCHUP_TYPES, type CatchUpType, type CatchUpWidgetSettings } from "./catchUp.js";

// ── Per-patient settings (D-b/D-c/D-d) ──────────────────────────────────────

/**
 * How the due-window is shaped (plan §6). The patient picks one in settings.
 *   due_overdue  — anchor in [now − hoursBefore, now + hoursAfter-as-grace];
 *                  won't surface a 10pm bedtime pill at 6pm.
 *   symmetric    — anchor in [now − hoursBefore, now + hoursAfter] (the literal
 *                  "give or take N hours"). DEFAULT.
 *   per_day_part — each day-part gets its own before/after window (Phase 2): the
 *                  `perDayPart` overrides below set a wider/later window for bedtime,
 *                  a tighter one for noon, etc. A group with no override falls back to
 *                  the global hoursBefore/hoursAfter.
 */
export const WINDOW_MODES = ["due_overdue", "symmetric", "per_day_part"] as const;
export type WindowMode = (typeof WINDOW_MODES)[number];

/** A before/after look-window in whole hours (one day-part's override, Phase 2). */
export interface DayPartWindow {
  hoursBefore: number;
  hoursAfter: number;
}

/** Symptom card display density (D-SF-R3, SYMPTOM_FOLLOWUP_REFINEMENT_PLAN.md).
 *  "full" → roomy single-control card (Option 1); "compact" → one-line rows (Option 3). */
export const SYMPTOM_CARD_DENSITY_OPTIONS = ["full", "compact"] as const;
export type SymptomCardDensity = (typeof SYMPTOM_CARD_DENSITY_OPTIONS)[number];

/** Check-in frequency options for symptom follow-up (D-SF-5, SYMPTOM_FOLLOWUP_PLAN.md). */
export const SYMPTOM_FOLLOWUP_FREQUENCY_OPTIONS = [
  "multiPerDay",
  "daily",
  "every3days",
  "weekly",
  "onOpen",
] as const;
export type SymptomFollowUpFrequency = (typeof SYMPTOM_FOLLOWUP_FREQUENCY_OPTIONS)[number];

/** Per-symptom reminder interval options in hours (D-SF-I2, SYMPTOM_REMINDER_INTERVAL_PLAN.md).
 *  Replaces the slot-based frequency engine: a symptom is due when
 *  now − lastUpdate ≥ its resolved interval (D-SF-I1). */
export const SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS =
  [1, 2, 4, 6, 12, 24, 48, 72, 96, 120, 144, 168] as const;
export type SymptomReminderIntervalHours =
  (typeof SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS)[number];
export const DEFAULT_SYMPTOM_REMINDER_INTERVAL_HOURS: SymptomReminderIntervalHours = 6;

/** "1h", "12h", "1d" … "7d" (D-SF-I2). */
export function formatReminderInterval(hours: number): string {
  return hours < 24 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

/** Clamp a stored per-symptom interval to a valid option, falling back to default (D-SF-I2). */
export function resolveSymptomReminderIntervalHours(
  intervals: Record<string, number> | undefined,
  key: string
): SymptomReminderIntervalHours {
  const v = intervals?.[key];
  return (SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS as readonly number[]).includes(v as number)
    ? (v as SymptomReminderIntervalHours)
    : DEFAULT_SYMPTOM_REMINDER_INTERVAL_HOURS;
}

/** Per-patient proactive-suggestion settings (absent → DEFAULT_PROACTIVE_SETTINGS). */
export interface ProactiveSuggestionSettings {
  enabled: boolean; // master toggle
  windowMode: WindowMode;
  hoursBefore: number; // dropdown in settings (global / non-per-day-part default)
  hoursAfter: number; // dropdown in settings (global / non-per-day-part default)
  includeLearned: boolean; // include the learned-from-history source (Source B, §4)
  /** Per-day-part window overrides, applied only when windowMode === "per_day_part"
   *  (Phase 2). Each group falls back to the global hoursBefore/hoursAfter when absent. */
  perDayPart?: Partial<Record<ScheduledGroup, DayPartWindow>>;
  /** How often (in days) to send "how is your injury?" reminders while status is
   *  active or recovering. null = Off (no reminders). undefined (absent) → default 3 days.
   *  D-PT-12 (PHYSICAL_THERAPY_PLAN.md). */
  injuryFollowUpIntervalDays?: 2 | 3 | 7 | 14 | null;

  // ── Symptom / pain follow-up settings (D-SF-*, SYMPTOM_FOLLOWUP_PLAN.md) ──

  /** Master toggle for symptom/pain follow-up cards. Absent → true (D-SF-5). */
  symptomFollowUpEnabled?: boolean;
  /** @deprecated D-SF-I1 (SYMPTOM_REMINDER_INTERVAL_PLAN.md) — no longer drives due-ness;
   *  kept so stored values keep validating. Superseded by symptomReminderIntervals. */
  symptomFollowUpFrequency?: SymptomFollowUpFrequency;
  /** @deprecated D-SF-I1 — no longer drives due-ness. Superseded by symptomReminderIntervals. */
  symptomCheckInsPerDay?: 1 | 2 | 3 | 4;
  /** Auto-close stale open symptoms after N days of no update. null = Off. Absent → 30 (D-SF-6). */
  symptomFollowUpAutoCloseDays?: number | null;
  /** Lookback window for the open-symptom scan in weeks. Absent → 2 (D-SF-8). */
  symptomFollowUpLookbackWeeks?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
  /** Snooze duration in hours for "Dismiss for now". Absent → 2 (D-SF-9). */
  symptomSnoozeHours?: number;
  /** Card display density. Absent → "full" (D-SF-R3, SYMPTOM_FOLLOWUP_REFINEMENT_PLAN.md). */
  symptomCardDensity?: SymptomCardDensity;
  /** Server-side snooze state: followUpKey → snoozedUntilIso (D-SF-9). Not user-configurable. */
  symptomSnoozes?: Record<string, string>;
  /** Per-symptom reminder interval overrides: followUpKey → hours (D-SF-I1/I2,
   *  SYMPTOM_REMINDER_INTERVAL_PLAN.md). Values must be members of
   *  SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS. Entries persist across resolution
   *  (D-SF-I7). Not edited in Preferences. */
  symptomReminderIntervals?: Record<string, number>;

  // ── Acute-illness follow-up settings (D-IE-*, ILLNESS_EPISODE_TRACKING_PLAN.md) ──

  /** Master toggle for illness follow-up cards. Absent → true (D-IE-3). */
  illnessFollowUpEnabled?: boolean;
  /** Auto-close stale open illnesses after N days of no update. null = Off. Absent → 21 (D-IE-10). */
  illnessFollowUpAutoCloseDays?: number | null;
  /** Lookback window for the open-illness scan, in weeks. Absent → 4 (D-IE-11). */
  illnessFollowUpLookbackWeeks?: 2 | 4 | 6 | 8 | 12 | 16;
  /** Snooze duration in hours for the illness card's "Dismiss for now". Absent → 12 (D-IE-12). */
  illnessSnoozeHours?: number;
  /** Server-side snooze state: episodeKey → snoozedUntilIso. Not user-configurable. */
  illnessSnoozes?: Record<string, string>;
  /** Per-illness reminder interval overrides: episodeKey → days. Values must be members of
   *  ILLNESS_REMINDER_INTERVAL_DAYS_OPTIONS. Not edited in Preferences. */
  illnessReminderIntervalDays?: Record<string, number>;

  /** Per-type catch-up widget toggles (CATCHUP_WIDGETS_PLAN.md D-CU-5). Absent key -> true. */
  catchUpWidgets?: CatchUpWidgetSettings;

  /** Master toggle for the AI persistent-symptom "see a doctor" alert
   *  (SYMPTOM_PERSISTENCE_ALERT_PLAN.md, D-SPA-1). Absent -> false (off by default). */
  symptomPersistenceAlertEnabled?: boolean;
  /** How often the background job re-evaluates this patient's open symptoms:
   *  1 = daily, 7 = weekly, 30 = monthly. Absent -> 7 once enabled (D-SPA-2). */
  symptomPersistenceAlertIntervalDays?: 1 | 7 | 30;
}

export const DEFAULT_PROACTIVE_SETTINGS: ProactiveSuggestionSettings = {
  enabled: true,
  windowMode: "symmetric",
  hoursBefore: 3,
  hoursAfter: 3,
  includeLearned: true,
  symptomFollowUpEnabled: true,
  symptomFollowUpFrequency: "multiPerDay",
  symptomCheckInsPerDay: 2,
  symptomFollowUpAutoCloseDays: 30,
  symptomFollowUpLookbackWeeks: 2,
  symptomSnoozeHours: 2,
  symptomCardDensity: "full",
  illnessFollowUpEnabled: true,
  illnessFollowUpAutoCloseDays: 21,
  illnessFollowUpLookbackWeeks: 4,
  illnessSnoozeHours: 12,
  symptomPersistenceAlertEnabled: false,
  symptomPersistenceAlertIntervalDays: 7,
};

/** Bounds for the hours-before/after dropdowns (settings UI + server clamp). */
export const PROACTIVE_HOURS_MIN = 1;
export const PROACTIVE_HOURS_MAX = 72;

/**
 * Discrete hour choices shown in the settings dropdowns. Every hour 1–12 for
 * fine control around a dose, then coarser long-range steps up to 72 h so a
 * dose taken up to three days ago can still surface (must stay within
 * [PROACTIVE_HOURS_MIN, PROACTIVE_HOURS_MAX] so it round-trips the clamp).
 */
export const PROACTIVE_HOURS_OPTIONS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 16, 24, 36, 48, 72,
] as const;

// ── Injury follow-up cadence (D-PT-12, PHYSICAL_THERAPY_PLAN.md Phase 5) ─────

/** Valid cadence options (days between "how is your injury?" reminders). */
export const INJURY_FOLLOWUP_INTERVAL_OPTIONS = [2, 3, 7, 14] as const;
export type InjuryFollowUpInterval = (typeof INJURY_FOLLOWUP_INTERVAL_OPTIONS)[number];

/** Default cadence when the field is absent from stored settings (D-PT-12). */
export const DEFAULT_INJURY_FOLLOWUP_INTERVAL_DAYS: InjuryFollowUpInterval = 3;

/**
 * Resolve the effective injury follow-up interval from the stored setting.
 * Returns `undefined` when explicitly Off (null), otherwise returns the stored
 * value if valid or the default (3 days) when absent/invalid.
 * Mirrors the resolveProactiveSettings/resolveDayPartTimes validation style.
 */
export function resolveInjuryFollowUpInterval(
  custom?: 2 | 3 | 7 | 14 | null
): number | undefined {
  if (custom === null) return undefined; // explicitly Off
  const valid = (INJURY_FOLLOWUP_INTERVAL_OPTIONS as readonly number[]).includes(custom as number);
  return valid ? (custom as InjuryFollowUpInterval) : DEFAULT_INJURY_FOLLOWUP_INTERVAL_DAYS;
}

// ── Symptom persistence alert cadence (D-SPA-2, SYMPTOM_PERSISTENCE_ALERT_PLAN.md) ──

export const SYMPTOM_PERSISTENCE_INTERVAL_OPTIONS = [1, 7, 30] as const;
export type SymptomPersistenceIntervalDays = (typeof SYMPTOM_PERSISTENCE_INTERVAL_OPTIONS)[number];
export const DEFAULT_SYMPTOM_PERSISTENCE_INTERVAL_DAYS: SymptomPersistenceIntervalDays = 7;
export const SYMPTOM_PERSISTENCE_INTERVAL_LABELS: Record<SymptomPersistenceIntervalDays, string> = {
  1: "Daily",
  7: "Weekly",
  30: "Monthly",
};

const clampHours = (n: unknown, fallback: number): number =>
  typeof n === "number" && Number.isFinite(n)
    ? Math.min(PROACTIVE_HOURS_MAX, Math.max(PROACTIVE_HOURS_MIN, Math.round(n)))
    : fallback;

/** Sanitize a patient's per-day-part overrides: keep only known groups, clamp hours. */
function resolvePerDayPart(
  custom?: Partial<Record<ScheduledGroup, Partial<DayPartWindow>>>
): Partial<Record<ScheduledGroup, DayPartWindow>> | undefined {
  if (!custom || typeof custom !== "object") return undefined;
  const out: Partial<Record<ScheduledGroup, DayPartWindow>> = {};
  for (const g of SCHEDULED_GROUPS) {
    const w = custom[g];
    if (!w || typeof w !== "object") continue;
    if (w.hoursBefore === undefined && w.hoursAfter === undefined) continue;
    out[g] = {
      hoursBefore: clampHours(w.hoursBefore, DEFAULT_PROACTIVE_SETTINGS.hoursBefore),
      hoursAfter: clampHours(w.hoursAfter, DEFAULT_PROACTIVE_SETTINGS.hoursAfter),
    };
  }
  return Object.keys(out).length ? out : undefined;
}

/** Sanitize a patient's catch-up widget toggles: keep only known CATCHUP_TYPES
 *  keys with boolean values; every key defaults to true (D-CU-5/D-CU-6). */
export function resolveCatchUpWidgets(custom?: unknown): Required<Record<CatchUpType, boolean>> {
  const raw = custom && typeof custom === "object" ? (custom as CatchUpWidgetSettings) : undefined;
  const out = {} as Required<Record<CatchUpType, boolean>>;
  for (const t of CATCHUP_TYPES) {
    const v = raw?.[t];
    out[t] = typeof v === "boolean" ? v : true;
  }
  return out;
}

/**
 * Merge a patient's partial proactive settings over the defaults. Mirrors
 * resolveDoseSafetySettings() / resolveDayPartTimes() so server + UI agree on the
 * default shape and on the hours clamp.
 */
export function resolveProactiveSettings(
  custom?: Partial<ProactiveSuggestionSettings>
): ProactiveSuggestionSettings {
  if (!custom) return { ...DEFAULT_PROACTIVE_SETTINGS };
  const perDayPart = resolvePerDayPart(custom.perDayPart);
  // null → explicitly Off; valid number → use it; absent/invalid → default
  const ri = custom.injuryFollowUpIntervalDays;
  const injuryFollowUpIntervalDays: 2 | 3 | 7 | 14 | null =
    ri === null
      ? null
      : (INJURY_FOLLOWUP_INTERVAL_OPTIONS as readonly number[]).includes(ri as number)
        ? (ri as InjuryFollowUpInterval)
        : DEFAULT_INJURY_FOLLOWUP_INTERVAL_DAYS;
  // ── Symptom follow-up settings (D-SF-*) ──
  const symptomFollowUpEnabled =
    typeof custom.symptomFollowUpEnabled === "boolean"
      ? custom.symptomFollowUpEnabled
      : DEFAULT_PROACTIVE_SETTINGS.symptomFollowUpEnabled!;

  const symptomFollowUpFrequency = (
    SYMPTOM_FOLLOWUP_FREQUENCY_OPTIONS as readonly string[]
  ).includes(custom.symptomFollowUpFrequency as string)
    ? (custom.symptomFollowUpFrequency as SymptomFollowUpFrequency)
    : DEFAULT_PROACTIVE_SETTINGS.symptomFollowUpFrequency!;

  const rawCipd = custom.symptomCheckInsPerDay;
  const symptomCheckInsPerDay: 1 | 2 | 3 | 4 = ([1, 2, 3, 4] as const).includes(rawCipd as 1)
    ? (rawCipd as 1 | 2 | 3 | 4)
    : DEFAULT_PROACTIVE_SETTINGS.symptomCheckInsPerDay!;

  const rawAcd = custom.symptomFollowUpAutoCloseDays;
  const symptomFollowUpAutoCloseDays: number | null =
    rawAcd === null
      ? null
      : typeof rawAcd === "number" && rawAcd > 0
      ? Math.round(rawAcd)
      : DEFAULT_PROACTIVE_SETTINGS.symptomFollowUpAutoCloseDays!;

  const rawLb = custom.symptomFollowUpLookbackWeeks;
  const symptomFollowUpLookbackWeeks: 1|2|3|4|5|6|7|8 = (
    [1,2,3,4,5,6,7,8] as const
  ).includes(rawLb as 1)
    ? (rawLb as 1|2|3|4|5|6|7|8)
    : DEFAULT_PROACTIVE_SETTINGS.symptomFollowUpLookbackWeeks!;

  const symptomSnoozeHours =
    typeof custom.symptomSnoozeHours === "number" && custom.symptomSnoozeHours > 0
      ? custom.symptomSnoozeHours
      : DEFAULT_PROACTIVE_SETTINGS.symptomSnoozeHours!;

  const symptomCardDensity: SymptomCardDensity = (
    SYMPTOM_CARD_DENSITY_OPTIONS as readonly string[]
  ).includes(custom.symptomCardDensity as string)
    ? (custom.symptomCardDensity as SymptomCardDensity)
    : DEFAULT_PROACTIVE_SETTINGS.symptomCardDensity!;

  // Prune past snooze entries on resolve so the map stays small
  const symptomSnoozes: Record<string, string> = {};
  if (custom.symptomSnoozes && typeof custom.symptomSnoozes === "object") {
    const nowIso = new Date().toISOString();
    for (const [k, v] of Object.entries(custom.symptomSnoozes)) {
      if (typeof v === "string" && v > nowIso) symptomSnoozes[k] = v;
    }
  }

  // Keep only valid per-symptom reminder intervals; never time-pruned (D-SF-I7).
  const symptomReminderIntervals: Record<string, number> = {};
  if (custom.symptomReminderIntervals && typeof custom.symptomReminderIntervals === "object") {
    for (const [k, v] of Object.entries(custom.symptomReminderIntervals)) {
      if ((SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS as readonly number[]).includes(v as number)) {
        symptomReminderIntervals[k] = v as number;
      }
    }
  }

  // ── Acute-illness follow-up settings (D-IE-*) ──
  const illnessFollowUpEnabled =
    typeof custom.illnessFollowUpEnabled === "boolean"
      ? custom.illnessFollowUpEnabled
      : DEFAULT_PROACTIVE_SETTINGS.illnessFollowUpEnabled!;

  const rawIacd = custom.illnessFollowUpAutoCloseDays;
  const illnessFollowUpAutoCloseDays: number | null =
    rawIacd === null
      ? null
      : typeof rawIacd === "number" && rawIacd > 0
      ? Math.round(rawIacd)
      : DEFAULT_PROACTIVE_SETTINGS.illnessFollowUpAutoCloseDays!;

  const rawIlb = custom.illnessFollowUpLookbackWeeks;
  const illnessFollowUpLookbackWeeks: 2 | 4 | 6 | 8 | 12 | 16 = (
    [2, 4, 6, 8, 12, 16] as const
  ).includes(rawIlb as 2)
    ? (rawIlb as 2 | 4 | 6 | 8 | 12 | 16)
    : DEFAULT_PROACTIVE_SETTINGS.illnessFollowUpLookbackWeeks!;

  const illnessSnoozeHours =
    typeof custom.illnessSnoozeHours === "number" && custom.illnessSnoozeHours > 0
      ? custom.illnessSnoozeHours
      : DEFAULT_PROACTIVE_SETTINGS.illnessSnoozeHours!;

  // Prune past illness snooze entries on resolve so the map stays small.
  const illnessSnoozes: Record<string, string> = {};
  if (custom.illnessSnoozes && typeof custom.illnessSnoozes === "object") {
    const nowIso = new Date().toISOString();
    for (const [k, v] of Object.entries(custom.illnessSnoozes)) {
      if (typeof v === "string" && v > nowIso) illnessSnoozes[k] = v;
    }
  }

  // Keep only valid per-illness reminder intervals; never time-pruned.
  const illnessReminderIntervalDays: Record<string, number> = {};
  if (custom.illnessReminderIntervalDays && typeof custom.illnessReminderIntervalDays === "object") {
    for (const [k, v] of Object.entries(custom.illnessReminderIntervalDays)) {
      if (([1, 2, 3, 5, 7, 14] as const).includes(v as 1)) {
        illnessReminderIntervalDays[k] = v as number;
      }
    }
  }

  // Keep only the explicitly-false catch-up toggles (every key defaults true,
  // so storing "true" entries would just bloat the object, per D-CU-5).
  const resolvedCatchUpWidgets = resolveCatchUpWidgets(custom.catchUpWidgets);
  const catchUpWidgets: CatchUpWidgetSettings = {};
  for (const t of CATCHUP_TYPES) {
    if (!resolvedCatchUpWidgets[t]) catchUpWidgets[t] = false;
  }

  // ── Symptom persistence alert settings (D-SPA-1/2) — always present, never null ──
  const symptomPersistenceAlertEnabled =
    typeof custom.symptomPersistenceAlertEnabled === "boolean"
      ? custom.symptomPersistenceAlertEnabled
      : DEFAULT_PROACTIVE_SETTINGS.symptomPersistenceAlertEnabled!;

  const rawSpaid = custom.symptomPersistenceAlertIntervalDays;
  const symptomPersistenceAlertIntervalDays: SymptomPersistenceIntervalDays = (
    SYMPTOM_PERSISTENCE_INTERVAL_OPTIONS as readonly number[]
  ).includes(rawSpaid as number)
    ? (rawSpaid as SymptomPersistenceIntervalDays)
    : DEFAULT_SYMPTOM_PERSISTENCE_INTERVAL_DAYS;

  return {
    enabled:
      typeof custom.enabled === "boolean" ? custom.enabled : DEFAULT_PROACTIVE_SETTINGS.enabled,
    windowMode: WINDOW_MODES.includes(custom.windowMode as WindowMode)
      ? (custom.windowMode as WindowMode)
      : DEFAULT_PROACTIVE_SETTINGS.windowMode,
    hoursBefore: clampHours(custom.hoursBefore, DEFAULT_PROACTIVE_SETTINGS.hoursBefore),
    hoursAfter: clampHours(custom.hoursAfter, DEFAULT_PROACTIVE_SETTINGS.hoursAfter),
    includeLearned:
      typeof custom.includeLearned === "boolean"
        ? custom.includeLearned
        : DEFAULT_PROACTIVE_SETTINGS.includeLearned,
    ...(perDayPart ? { perDayPart } : {}),
    injuryFollowUpIntervalDays,
    symptomFollowUpEnabled,
    symptomFollowUpFrequency,
    symptomCheckInsPerDay,
    symptomFollowUpAutoCloseDays,
    symptomFollowUpLookbackWeeks,
    symptomSnoozeHours,
    symptomCardDensity,
    ...(Object.keys(symptomSnoozes).length ? { symptomSnoozes } : {}),
    ...(Object.keys(symptomReminderIntervals).length ? { symptomReminderIntervals } : {}),
    illnessFollowUpEnabled,
    illnessFollowUpAutoCloseDays,
    illnessFollowUpLookbackWeeks,
    illnessSnoozeHours,
    ...(Object.keys(illnessSnoozes).length ? { illnessSnoozes } : {}),
    ...(Object.keys(illnessReminderIntervalDays).length ? { illnessReminderIntervalDays } : {}),
    ...(Object.keys(catchUpWidgets).length ? { catchUpWidgets } : {}),
    symptomPersistenceAlertEnabled,
    symptomPersistenceAlertIntervalDays,
  };
}

/**
 * The before/after look-window (in whole hours) for a given day-part group. When
 * windowMode is "per_day_part" and the group has an override, that wins; otherwise
 * the global hoursBefore/hoursAfter apply. Shared by the engine (window math) and the
 * settings UI (showing the effective values), so both agree. The due_overdue
 * look-ahead cap is applied by the engine, not here.
 */
export function proactiveWindowFor(
  s: ProactiveSuggestionSettings,
  group?: ScheduledGroup
): DayPartWindow {
  if (s.windowMode === "per_day_part" && group && s.perDayPart?.[group]) {
    return { ...s.perDayPart[group]! };
  }
  return { hoursBefore: s.hoursBefore, hoursAfter: s.hoursAfter };
}

// ── Wire shapes (API → web) ─────────────────────────────────────────────────

/** What kind of thing produced a suggestion (drives the headline/styling). */
export const PROACTIVE_SUGGESTION_KINDS = [
  "scheduled_medication",
  "learned_medication",
  "pt_compliance",
  "injury_follow_up",
  "symptom_follow_up",
  "symptom_persistence_alert",
  "illness_follow_up",
] as const;
export type ProactiveSuggestionKind = (typeof PROACTIVE_SUGGESTION_KINDS)[number];

/**
 * Suggestion shape (plan §9):
 *   prefilled — we know the value ahead of time (a regimen's dose), so we can
 *               offer a one-tap confirmable event. Medications fit here.
 *   reminder  — we can't know today's value (e.g. today's blood pressure), so the
 *               suggestion is a nudge that seeds the chat / quick-entry (Phase 3+).
 */
export const PROACTIVE_SUGGESTION_SHAPES = ["prefilled", "reminder"] as const;
export type ProactiveSuggestionShape = (typeof PROACTIVE_SUGGESTION_SHAPES)[number];

/**
 * One proposed event inside a prefilled suggestion, as it crosses the wire. Mirrors
 * the API's internal `Proposed` (eventType/occurredAt/payload/confirmation) plus the
 * `scheduledAnchorAt` the Now/Scheduled-time radio toggles between (plan §10).
 */
export interface SuggestedEvent {
  eventType: string;
  occurredAt: string; // default = now at suggest time (the "Now" radio value)
  payload: Record<string, unknown>;
  confirmation: string;
  /** The med's scheduled / learned typical time — the "Scheduled time" radio value. */
  scheduledAnchorAt?: string;
  /** Which source produced this event (Phase 2): "scheduled" came from a saved
   *  regimen; "learned" was inferred from history and can be offered an
   *  "add to a regimen?" nudge. Absent on non-medication suggestions. */
  source?: "scheduled" | "learned";
}

/**
 * A single proactive suggestion returned to the client. Generic over the proposal
 * type so the server can carry its richer internal `Proposed` while the wire/web
 * use `SuggestedEvent`.
 */
export interface ProactiveSuggestion<TProposal = SuggestedEvent> {
  /** Stable id for snooze/dedupe, e.g. "scheduled_medication:<regimenId>". */
  id: string;
  kind: ProactiveSuggestionKind;
  shape: ProactiveSuggestionShape;
  headline: string;
  /** Present for shape="prefilled". */
  proposals?: TProposal[];
  /** Present for shape="reminder" (Phase 3+). */
  prompt?: string;
  /** Present for kind="symptom_follow_up" (SYMPTOM_FOLLOWUP_PLAN.md). */
  openSymptoms?: import("./symptomFollowUp.js").OpenSymptom[];
  /** Present for kind="illness_follow_up" (ILLNESS_EPISODE_TRACKING_PLAN.md). */
  openIllnesses?: import("./illnessEpisodes.js").OpenIllness[];
  /** Present for kind="symptom_persistence_alert" (SYMPTOM_PERSISTENCE_ALERT_PLAN.md). */
  persistenceAlert?: {
    key: string; // D-SF-2 identity key — pass back to the dismiss endpoint
    name: string;
    daysPersisting: number;
    onsetAt: string;
    reasoning: string; // the AI's short rationale, shown as secondary/detail text
  };
}

// ── Debug / diagnostics (admin-only "why is this suggested?" inspector) ───────
// Shapes for the admin debug tool on the chat page: when an admin clicks "Debug"
// on the proactive medication card, the server re-runs the engine in an explain
// mode that records, per evaluated candidate, the timing math and the human-
// readable reason it was (or was not) surfaced. Used to author a copy-pasteable
// prompt for an AI to diagnose suggestion accuracy. Pure data — crosses the wire.

/** The before/after look-window, both in hours (as configured) and minutes (as used). */
export interface ProactiveDebugWindow {
  hoursBefore: number;
  hoursAfter: number;
  beforeMin: number;
  afterMin: number;
}

/** A single recent dose of a medication, as seen by the engine (newest first). */
export interface ProactiveDebugDose {
  occurredAt: string; // ISO instant the dose was logged at
  localTime: string; // "YYYY-MM-DD HH:MM" in the patient's timezone
  dose?: string; // e.g. "10 mg" when present on the payload
  scheduledGroup?: string;
  missed?: boolean;
}

/**
 * One medication the engine evaluated as a possible suggestion, with the full
 * timing math and the reason(s) it was included or excluded. Covers BOTH sources
 * so false positives (shown but shouldn't be) and false negatives (should show but
 * doesn't) are both debuggable.
 */
export interface ProactiveDebugCandidate {
  name: string;
  source: "scheduled" | "learned";
  group: ScheduledGroup;
  included: boolean;
  /** Human-readable reasons (each a complete sentence) explaining the verdict. */
  reasons: string[];

  // ── Scheduled-source detail (present when source === "scheduled") ──
  regimenId?: string;
  regimenName?: string;
  regimenKind?: string;
  regimenActive?: boolean;
  defaultTime?: string; // the regimen's "HH:MM" hint, if set
  /** When the candidate was driven by an explicit per-item schedule
   *  (MEDICATION_SCHEDULE_PLAN.md), the one-line summary, e.g. "Every 2 days · 8:00 AM". */
  scheduleSummary?: string;

  // ── Learned-source detail (present when source === "learned") ──
  occurrencesInWindow?: number; // in-window, non-missed doses counted
  distinctDaysInWindow?: number; // distinct local days those doses fell on
  spanDays?: number; // observed span (capped at the learned window)
  regularity?: number; // distinctDays / expectedOnDays (0..1), interval-aware
  minOccurrencesRequired?: number;
  regularityRequired?: number;
  /** Modal inter-dose gap in days detected from history (1 = daily, 2 = every other day…). */
  detectedGapDays?: number;
  /** Expected on-days in the span given the detected gap (spanDays / detectedGapDays). */
  expectedOnDays?: number;
  /** Whether the relative every-N-days due gate passed (days since last dose ≥ gap). */
  dueByInterval?: boolean;

  // ── Common timing math ──
  anchorTime?: string; // "HH:MM" local anchor (scheduled time or learned average)
  anchorIso?: string; // the anchor resolved to today's local date, as an ISO instant
  anchorMinuteOfDay?: number; // 0..1439
  window: ProactiveDebugWindow; // the effective window for this candidate's group
  deltaMinutes?: number; // signed minutes from now to the anchor (circular)
  inWindow?: boolean;
  alreadyHandled?: boolean; // a matching dose was logged/missed in the window
  claimedByScheduled?: boolean; // learned skipped because a regimen already claimed it
  inactive?: boolean; // med is on the paused/inactive list

  /** The last N logged doses of this med (newest first) the engine read. */
  recentDoses: ProactiveDebugDose[];
}

/**
 * The complete diagnostic snapshot for a patient's proactive medication suggestions
 * at a moment in time. Authored into a prompt by the web debug modal.
 */
export interface ProactiveSuggestionDebug {
  now: string; // ISO instant the engine evaluated at
  timezone: string;
  nowLocalTime: string; // "YYYY-MM-DD HH:MM" in the patient's timezone
  nowMinuteOfDay: number; // 0..1439
  settings: ProactiveSuggestionSettings;
  globalWindow: ProactiveDebugWindow; // the non-per-day-part window
  dayPartTimes: Record<ScheduledGroup, string>; // resolved (defaults merged)
  inactiveMedNames: string[];
  tuning: {
    learnedWindowDays: number;
    learnedMinOccurrences: number;
    learnedRegularity: number;
    handledBeforeCapMinutes: number;
    doneEndGraceMinutes: number;
  };
  /** Engine-level notes (e.g. suggestions disabled, cold-start). */
  notes: string[];
  /** Every medication evaluated — included first, then excluded. */
  candidates: ProactiveDebugCandidate[];
}
